import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { NavLink } from 'react-router-dom';
import ReactTimeout from 'react-timeout'; // TODO: lodash => debounce

import axios from 'axios_client';
import AppData from 'app_data';
import Theme from 'theme';
import Utils from 'utils/common_utils';
import type { ChartFilters } from 'hooks/useAdminGroup/applyLocationScope';

export interface CallFilterOption {
  name: string;
  label: string;
  route?: string;
}

type CallFilterTotals = Record<string, number | string | undefined>;

export interface CallFiltersProps {
  /** The time range being represented by the table */
  timeRange: Record<string, unknown>;
  /** Whether or not the data is in real time */
  realTime: boolean;
  /** The filter option */
  filter: string;
  /** An array of all filtering options */
  filterOptions: CallFilterOption[];
  /** The section being displayed */
  section: string;
  /** The URL for the data with filters */
  filterTotalsUrl: string;
  /** The filters for the call log */
  filters?: ChartFilters;
  /** True while the breadcrumb scope is still resolving -- hold fetches */
  scopeHold?: boolean;
  /** Current route path (mapped from the router state by connect) */
  pathname: string;
}

interface CallFiltersState {
  values: CallFilterTotals | null;
  intervalID: number | null;
}

class CallFilters extends Component<CallFiltersProps, CallFiltersState> {
  state: CallFiltersState = {
    values: null,
    intervalID: null,
  };

  componentDidMount() {
    this.updateFilterValues();
  }

  componentWillUnmount() {
    if(this.state.intervalID) {
      clearInterval(this.state.intervalID);
    }
  }

  componentDidUpdate(prevProps: CallFiltersProps) {
    // Only these props feed the totals request (buildFilterQuery plus the
    // hold/real-time plumbing) -- refetch only when one actually changed.
    // Reference equality is reliable: timeRange and filterOptions come from
    // the immutability-helper reducer, and filters is the memoized
    // scopedApiFilters from useScopedApiFilters. A chip-filter/pathname
    // change deliberately does NOT refetch (the totals don't depend on the
    // selected chip), and a section change unmounts this component entirely
    // (setInactive -> ready=false), so componentDidMount covers it. State
    // updates (values/intervalID) land here too; the prop diff keeps them
    // from looping.
    const fetchInputsChanged =
      prevProps.timeRange !== this.props.timeRange ||
      prevProps.filters !== this.props.filters ||
      prevProps.filterOptions !== this.props.filterOptions ||
      prevProps.filterTotalsUrl !== this.props.filterTotalsUrl ||
      prevProps.realTime !== this.props.realTime ||
      prevProps.scopeHold !== this.props.scopeHold;

    if (fetchInputsChanged) {
      this.updateFilterValues();
    }
  }

  // Monotonic id of the latest FIRED totals request. There is no
  // cancellation (raw axios client), so overlapping requests can land out of
  // order; only the newest may commit.
  fetchSeq = 0;

  buildFilterQuery = (props: CallFiltersProps) => ({
    selects: _.map(props.filterOptions, (filter) => (
      { metric: filter.name, label: filter.label }
    )),
    unit: 'calls',
    time_range: props.timeRange,
    filters: props.filters,
  });

  fetchValues = (props: CallFiltersProps) => {
    // Guarded here as well as in updateFilterValues so a live real-time
    // interval can't fire while a mid-session scope change is resolving.
    if (props.scopeHold) {
      return;
    }

    const seq = ++this.fetchSeq;
    axios.post(props.filterTotalsUrl, this.buildFilterQuery(props)).then((response) => {
      // A newer request has fired since (scope/params changed); this
      // response is stale. Drop it.
      if (seq !== this.fetchSeq) {
        return;
      }
      this.setState({ values: response.data.totals });
    });
  };

  updateFilterValues = () => {
    const props = this.props;

    // Never fire while the breadcrumb scope is still resolving: the wrapper
    // re-renders when the hold clears and componentDidUpdate refetches.
    if (props.scopeHold) {
      return;
    }

    this.setState({ values: null });

    if (props.realTime) {
      this.fetchValues(props);

      if(!this.state.intervalID) {
        // Rebuild the query from current props on every tick -- a
        // closure-captured query would keep posting the filters (and scope)
        // from interval-creation time.
        const intervalID = window.setInterval(() => {
          this.fetchValues(this.props);
        }, AppData.user.refresh_rate * 1000);

        this.setState({ intervalID: intervalID });
      }
    } else {
      if(this.state.intervalID) {
        clearInterval(this.state.intervalID);
        this.setState({ intervalID: null });
      }
      this.fetchValues(props);
    }
  };

  render() {
    const { pathname } = this.props;
    const path = pathname.split('/');

    const callFilters = _.map(this.props.filterOptions, (_item, index) => {
      const item = _.assign({}, _item);
      const values = this.state.values;
      let value: React.ReactNode = values && values[item.label];
      if (value) {
        value = Utils.numberWithCommas(value);
      }

      if (item.name === 'total') item.route = '';

      return (
        <li key={index}>
          <NavLink exact to={`/reporting/${this.props.section}/${item.route}`} activeClassName="active">
            <div className={!path[3] && !item.route ? 'bold' : undefined}>{item.label}</div>
            <div style={{ color: Theme.getColor(item.name) }}>
              {value || '-'}
            </div>
          </NavLink>
        </li>
      );
    });
    if (this.props.filterOptions.length && this.props.filterOptions[0].name === 'total') {
      const divider = <li key="divider" className="divider"><div /></li>;
      callFilters.splice(1, 0, divider);
    }

    return (
      <ul className="call-filters">
        {callFilters}
      </ul>
    );
  }
}

// ReactTimeout has no types (shimmed as any); pin the wrapped component's
// props back so connect can infer ownProps = CallFiltersProps minus pathname.
const TimedCallFilters = ReactTimeout(CallFilters) as React.ComponentType<CallFiltersProps>;

export default connect((state: { router: { location: { pathname: string } } }) => ({
  pathname: state.router.location.pathname,
}))(TimedCallFilters);
